/****************************************************************************
 * Copyright 2021 EPAM Systems
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 ***************************************************************************/

import { applyMiddleware, combineReducers, createStore } from 'redux';
import { load, onAction } from './shared';
import optionsReducer, { initOptionsState } from './options';
import templatesReducer, { initTmplsState } from './templates';
import abbreviationLookupReducer from './abbreviationLookup';
import commonReducer from './common';

import actionStateReducer from './action';
import functionalGroupsReducer from './functionalGroups';
import saltsAndSolventsReducer from './saltsAndSolvents';
import { logger } from 'redux-logger';
import modalReducer from './modal';
import { pick } from 'lodash/fp';
import requestReducer from './request';
import { thunk } from 'redux-thunk';
import toolbarReducer from './toolbar';
import floatingToolsReducer from './floatingTools';
import notificationsReducer, { initNotificationsState } from './notifications';

export { onAction, load };

export const SET_SERVER = 'SET_SERVER';

const shared = combineReducers({
  common: commonReducer,
  actionState: actionStateReducer,
  toolbar: toolbarReducer,
  modal: modalReducer,
  abbreviationLookup: abbreviationLookupReducer,
  server: (store = null) => store,
  editor: (store = null) => store,
  options: optionsReducer,
  templates: templatesReducer,
  functionalGroups: functionalGroupsReducer,
  saltsAndSolvents: saltsAndSolventsReducer,
  requestsStatuses: requestReducer,
  floatingTools: floatingToolsReducer,
  notifications: notificationsReducer,
});

function getRootReducer(setEditor) {
  return function root(state, action) {
    let updatedState = state;

    switch (action.type) {
      case 'INIT': {
        setEditor(action.editor);
        // Extract action data (excluding type) and merge into state
        const data = { ...action };
        delete data.type;
        if (data) {
          updatedState = { ...updatedState, ...data };
        }
        // Set server
        updatedState = {
          ...updatedState,
          server: action.server || updatedState.server,
        };
        break;
      }

      case 'UPDATE': {
        const data = { ...action };
        delete data.type;
        if (data) {
          updatedState = { ...updatedState, ...data };
        }
        break;
      }

      case SET_SERVER: {
        updatedState = {
          ...updatedState,
          server: action.server || updatedState.server,
        };
        break;
      }
    }

    const sh = shared(updatedState, {
      ...action,
      ...pick(['editor', 'server', 'options'], updatedState),
    });

    const finalState =
      sh === updatedState.shared
        ? updatedState
        : {
            ...updatedState,
            ...sh,
          };

    // TODO: temporary solution. Need to review work with redux store
    global.currentState = finalState;
    return finalState;
  };
}

export default function (options, server, setEditor) {
  const { buttons = {}, customButtons, ...restOptions } = options;

  // TODO: redux localStorage here
  const initState = {
    actionState: null,
    editor: null,
    modal: null,
    options: Object.assign(initOptionsState, {
      app: restOptions,
      buttons,
      customButtons,
    }),
    server: server || Promise.reject(new Error('Standalone mode!')),
    templates: initTmplsState,
    notifications: initNotificationsState,
  };

  const middleware = [thunk];
  if (
    process.env.NODE_ENV !== 'production' &&
    process.env.KETCHER_ENABLE_REDUX_LOGGER === 'true'
  ) {
    middleware.push(logger);
  }

  const rootReducer = getRootReducer(setEditor);
  return createStore(rootReducer, initState, applyMiddleware(...middleware));
}

export function setServer(server) {
  return {
    type: SET_SERVER,
    server,
  };
}
